import { useState } from 'react';
import { flushSync } from 'react-dom';
import { FileDown, Printer, Send } from 'lucide-react';
import { A4_HEIGHT_PX, A4_WIDTH_PX, fitScale, renderPreview } from '@core/doc/preview';
import { printCapabilities, printDocument, saveDocumentPdf } from '@data/services/printService';
import { IssueTransitionError } from '@data/services/issueTransitionService';
import { useIssueDocument } from '@features/documents/useDocuments';
import { toast } from '@ui/components/Toaster';
import { useSalesOrder } from './useSalesOrders';

const assets = new URL('assets/', document.baseURI).href;

export function SalesOrderDocumentScreen({ documentId }: { documentId: string }) {
  const query = useSalesOrder(documentId);
  const issueMutation = useIssueDocument();
  const capabilities = printCapabilities();
  const [busy, setBusy] = useState(false);
  const scale = fitScale(window.innerWidth * .64);
  if (query.isLoading) return <p className="p-4 text-sm text-slate-400">Loading sales order…</p>;
  if (query.isError || !query.data) return <p role="alert" className="p-4 text-sm text-red-300">Could not open this sales order.</p>;
  const { model, stored, business } = query.data;
  const html = renderPreview(model, business, { scale }, assets);

  async function runPrint() {
    setBusy(true);
    try {
      await printDocument(model, business, assets);
    } catch (error) {
      toast({ title: 'Could not print sales order', description: error instanceof Error ? error.message : 'Try again.', tone: 'danger' });
    } finally { flushSync(() => setBusy(false)); }
  }

  async function runIssue() {
    setBusy(true);
    try {
      await issueMutation.mutateAsync(documentId);
      await query.refetch();
      toast({ title: `Sales order ${stored.docNumber} issued`, description: 'No stock, payment or receivable was created.', tone: 'success' });
    } catch (error) {
      toast({ title: 'Could not issue sales order', description: error instanceof IssueTransitionError || error instanceof Error ? error.message : 'Try again.', tone: 'danger' });
    } finally { setBusy(false); }
  }

  async function runSavePdf() {
    setBusy(true);
    try {
      const result = await saveDocumentPdf(model, business, assets);
      if (result.saved) toast({ title: 'PDF saved', description: result.filePath, tone: 'success' });
    } catch (error) {
      toast({ title: 'Could not save sales order PDF', description: error instanceof Error ? error.message : 'Try again.', tone: 'danger' });
    } finally { setBusy(false); }
  }

  return <div className="flex flex-col items-center gap-3 p-4 text-slate-100">
    <header className="flex w-full max-w-4xl flex-wrap items-center justify-between gap-3"><div><h1 className="font-semibold">Sales Order {stored.docNumber}</h1><p data-testid="sales-order-status" className="text-xs text-slate-400">{stored.status === 'DRAFT' ? 'Draft' : 'Issued'} · no stock reservation or receivable</p>{stored.quotationId && <p className="text-xs text-slate-500">Converted from quotation {stored.quotationId}</p>}</div><div className="flex flex-wrap gap-2">{stored.status === 'DRAFT' && <><a className="rounded border border-slate-700 px-3 py-2 text-sm" href={`#/sales/orders/${documentId}/edit`}>Edit draft</a><button data-testid="issue-sales-order" disabled={busy} onClick={() => void runIssue()} className="rounded bg-emerald-700 px-3 py-2 text-sm"><Send className="mr-1 inline h-4 w-4" />Issue sales order</button></>}{/*
        The dispatch affordance. It appears only for an ISSUED order, because a
        delivery note consumes an order that has been issued — the service refuses
        anything else, so offering the button earlier would teach a rule by
        refusing it. The link, not a mutation: it opens the dispatch editor with
        this order as its source and moves no stock until the note is issued.
      */}{stored.status === 'ISSUED' && <a data-testid="create-delivery-note" className="rounded border border-slate-700 px-3 py-2 text-sm" href={`#/sales/deliveries/from-order/${documentId}`}>Create delivery note</a>}<button disabled={busy} onClick={() => void runPrint()} className="rounded bg-brand-500 px-3 py-2 text-sm"><Printer className="mr-1 inline h-4 w-4" />Print / Reprint</button>{capabilities.silentPdf && <button disabled={busy} onClick={() => void runSavePdf()} className="rounded border border-slate-700 px-3 py-2 text-sm"><FileDown className="mr-1 inline h-4 w-4" />Save PDF</button>}</div></header>
    <iframe title="Sales order preview" data-testid="sales-order-preview" srcDoc={html} style={{ width: A4_WIDTH_PX * scale, height: A4_HEIGHT_PX * scale, border: 0 }} />
    <p className="text-xs text-slate-500">Preview · fulfilment is recorded in a later Delivery Note</p>
    <a className="text-sm text-brand-400 underline" href="#/sales/orders">Back to sales orders</a>
  </div>;
}
